import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Home, Briefcase, MessageSquare, User, Sparkles, BarChart3, Plus, Users } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { useAppShell } from "../context/AppShellContext";
import { useUnreadMessages } from "../context/UnreadMessagesContext";
import { useJobNotifications } from "../context/JobNotificationsContext";

// Single global mobile bottom navigation — the shell container never
// changes, only the items inside it change per role/route. Guests and
// candidates get their app-wide nav; employers get a lightweight nav
// everywhere else and hand off to their own 5 dashboard tabs (registered via
// AppShellContext) while on /company/dashboard.
export default function BottomNav() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const { employerNav } = useAppShell();
  const { unreadCount } = useUnreadMessages();
  const { unreadCount: jobUnreadCount } = useJobNotifications();
  const unreadBadge = unreadCount > 99 ? "99+" : unreadCount > 0 ? String(unreadCount) : null;
  // Avisos laborales (postulantes/entrevistas/ofertas) — badge AZUL
  // institucional, deliberadamente separado del badge ROSA de Mensajes.
  const jobBadge = jobUnreadCount > 99 ? "99+" : jobUnreadCount > 0 ? String(jobUnreadCount) : null;

  // CV Builder owns its own dedicated mobile full-screen header/bars.
  // /company-view stays inside the shell (Perfil marked active below).
  if (location.pathname === "/cv-builder") return null;

  const searchParams = new URLSearchParams(location.search);
  const currentTab = searchParams.get("tab") || "";
  const isDashboard = location.pathname === "/dashboard";
  const isCompanyDashboard = location.pathname === "/company/dashboard";
  const isCompanyPreview = location.pathname === "/company-view";

  let items;
  if (user?.role === "employer") {
    if (isCompanyDashboard && employerNav) {
      const { tab, setTab, startNewJob } = employerNav;
      items = [
        { id: "overview", label: t("bottomNav.overview"), icon: BarChart3, active: tab === "overview",
          onClick: () => { setTab("overview"); window.scrollTo(0, 0); } },
        { id: "jobs", label: t("bottomNav.listings"), icon: Briefcase, active: tab === "jobs",
          onClick: () => { setTab("jobs"); window.scrollTo(0, 0); } },
        { id: "post", label: t("bottomNav.post"), icon: Plus, active: tab === "post",
          onClick: () => startNewJob() },
        { id: "applicants", label: t("bottomNav.applicants"), icon: Users, active: tab === "applicants",
          onClick: () => { setTab("applicants"); window.scrollTo(0, 0); }, badge: jobBadge, badgeColor: "#028CF8" },
        { id: "messages", label: t("bottomNav.messages"), icon: MessageSquare, to: "/messages", badge: unreadBadge },
      ];
    } else {
      items = [
        { id: "overview", label: t("bottomNav.overview"), icon: BarChart3, to: "/company/dashboard?tab=overview" },
        { id: "jobs", label: t("bottomNav.listings"), icon: Briefcase, to: "/company/dashboard?tab=jobs" },
        { id: "post", label: t("bottomNav.post"), icon: Plus, to: "/company/dashboard?tab=post" },
        { id: "applicants", label: t("bottomNav.applicants"), icon: Users, to: "/company/dashboard?tab=applicants", badge: jobBadge, badgeColor: "#028CF8" },
        { id: "messages", label: t("bottomNav.messages"), icon: MessageSquare,
          to: "/messages", active: location.pathname === "/messages", badge: unreadBadge },
      ];
    }
  } else {
    items = [
      { id: "home", label: t("bottomNav.home"), icon: Home, to: "/",
        active: location.pathname === "/" },
      { id: "myjobs", label: t("bottomNav.myJobs"), icon: Briefcase,
        to: user ? "/dashboard?tab=myjobs" : "/auth",
        active: isDashboard && currentTab === "myjobs", badge: jobBadge, badgeColor: "#028CF8" },
      { id: "messages", label: t("bottomNav.messages"), icon: MessageSquare,
        to: user ? "/messages" : "/auth",
        active: location.pathname === "/messages", badge: unreadBadge },
      { id: "profile", label: t("bottomNav.profile"), icon: User,
        to: user ? "/dashboard?tab=profile" : "/auth",
        active: (isDashboard && currentTab === "profile") || isCompanyPreview },
    ];
  }

  const gridClass = items.length === 5 ? "grid-cols-5" : items.length === 3 ? "grid-cols-3" : "grid-cols-4";
  // Shared active-state pill — SAME fixed width/height for every item within
  // a given layout, so the translucent active background is always visually
  // identical. 4-item layout (candidate/guest) gets a wider pill (more room
  // per item, avoids label truncation); 5-item employer layout keeps its
  // original width since it still needs to fit 5 columns.
  const pillWidthClass = items.length === 4 ? "w-[90px] max-[359px]:w-[78px]" : "w-16 max-[359px]:w-14";
  const pillClass = `flex flex-col items-center justify-center gap-0.5 h-11 rounded-2xl transition-colors ${pillWidthClass}`;
  return (
    <nav
      data-testid="bottom-nav"
      className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-[#028CF8] shadow-[0_-2px_12px_rgba(15,23,42,0.15)]"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className={`grid ${gridClass} h-16`}>
        {items.map(({ id, label, icon: Icon, to, active, onClick, badge, badgeColor }) => {
          const itemClass = "h-full flex items-center justify-center min-w-0 px-0 overflow-hidden active:bg-white/10 transition-colors";
          const inner = (
            <div className={`${pillClass} ${active ? "bg-white/18" : ""}`}>
              <span className="relative inline-flex">
                <Icon className="h-6 w-6 text-white" strokeWidth={active ? 2.5 : 2} style={{ opacity: active ? 1 : 0.75 }} />
                {badge && (
                  <span data-testid={`bottom-nav-${id}-badge`}
                    className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 rounded-full text-white text-[9px] font-bold flex items-center justify-center leading-none"
                    style={{ background: badgeColor || "#F55FA5", boxShadow: "0 0 0 1.5px #FFFFFF" }}>
                    {badge}
                  </span>
                )}
              </span>
              <span className={`text-[11px] max-[359px]:text-[9.5px] leading-[1.1] tracking-[-0.02em] font-poppins whitespace-nowrap truncate max-w-full text-white ${active ? "font-bold" : "font-semibold"}`}
                style={{ opacity: active ? 1 : 0.75 }}>
                {label}
              </span>
            </div>
          );
          return onClick ? (
            <button key={id} type="button" onClick={onClick} data-testid={`bottom-nav-${id}`} className={itemClass}>
              {inner}
            </button>
          ) : (
            <Link key={id} to={to} data-testid={`bottom-nav-${id}`} className={itemClass}>
              {inner}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
